import { jsx as _jsx } from "react/jsx-runtime";
// NumberInput.tsx — the integer field the editor uses everywhere. Three behaviours the plain <input> did
// not give us:
//   * focusing selects the whole value, so typing REPLACES it (the field asked for most often).
//   * Ctrl+A selects too. On macOS Ctrl+A means "go to line start", so a typed digit used to land in
//     FRONT of the old value — the box read 512 when the author meant 5.
//   * the box can be emptied while typing. A controlled <input value={number}> cannot: an unparseable
//     draft never commits, React rewrites the old text, and backspace appears to do nothing.
// The draft is local text; only a parseable draft commits, and blur drops it so the stored value shows.
import { Component } from "react";
// The click-to-select mousedown/mouseup guard below, factored out so it has exactly one definition. The
// per-wedge mystery-chance inputs in LevelEditorPage (renderGeneration) hit the identical bug but
// deliberately don't use NumberInput — they handle decimals and a comma decimal separator through their own
// draft state — so they import this instead of carrying a second copy that could quietly drift from this
// one.
//
// onFocus + select() alone only works for tab-to-focus: in Blink and WebKit the mouseup that ENDS the click
// which focused the field collapses that selection back to a caret (Gecko doesn't), so clicking into the
// box left the value un-selected and typing appended instead of replacing. Suppressing the default on that
// one mouseup keeps the selection.
//
// Which mouseup that is has to be decided at mousedown time — by then focus has not moved yet, so
// `document.activeElement` still shows whether this click is the one bringing focus in. A mouseup in an
// already-focused field is left alone, so placing the caret and dragging out a selection still work.
//
// One instance holds one input's worth of state — a component rendering several such inputs (e.g. one per
// wedge) needs one instance per input, not one shared across all of them.
export class SelectOnClickGuard {
    constructor() {
        this.wasFocusedOnMouseDown = true;
        this.onMouseDown = (e) => {
            this.wasFocusedOnMouseDown = document.activeElement === e.currentTarget;
        };
        this.onMouseUp = (e) => {
            if (!this.wasFocusedOnMouseDown) {
                e.preventDefault();
                this.wasFocusedOnMouseDown = true; // the click is over; later mouseups are ordinary ones
            }
        };
    }
}
export class NumberInput extends Component {
    constructor() {
        super(...arguments);
        this.state = { draft: null };
        this.selectGuard = new SelectOnClickGuard();
        this.onFocus = (e) => {
            e.currentTarget.select();
        };
        this.onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === "a" || e.key === "A")) {
                e.preventDefault();
                e.currentTarget.select();
                return;
            }
            if (this.props.commitOn !== "blur") {
                return;
            }
            if (e.key === "Enter") {
                e.preventDefault();
                if (this.state.draft !== null) {
                    this.commit(this.state.draft);
                }
                this.setState({ draft: null });
                return;
            }
            if (e.key === "Escape") {
                // Drop what was typed; the stored value shows again. Deliberately NOT stopped from propagating
                // — the page's own Escape (close the menu, clear the selection) still has to run.
                this.setState({ draft: null });
            }
        };
        this.onChange = (e) => {
            const raw = e.target.value;
            this.setState({ draft: raw });
            if (this.props.commitOn !== "blur") {
                this.commit(raw);
            }
        };
        this.onBlur = () => {
            if (this.props.commitOn === "blur" && this.state.draft !== null) {
                this.commit(this.state.draft);
            }
            this.setState({ draft: null });
        };
    }
    clamp(v) {
        const { min, max } = this.props;
        let out = v;
        if (min !== undefined) {
            out = Math.max(min, out);
        }
        if (max !== undefined) {
            out = Math.min(max, out);
        }
        return out;
    }
    // Hands the draft text to the owner, if it parses. An empty or unparseable draft commits nothing — the
    // box can be cleared while typing without the template seeing a 0 or a NaN.
    commit(raw) {
        const v = parseInt(raw, 10);
        if (raw.trim() !== "" && !Number.isNaN(v)) {
            this.props.onChange(this.clamp(v));
        }
    }
    render() {
        // type="text" + inputMode="numeric": a number input shows spinners, and its arrow keys would fight
        // the editor's own arrow-key shortcut. Everything typed here is an integer anyway.
        return (_jsx("input", { type: "text", inputMode: "numeric", "aria-label": this.props.ariaLabel, value: this.state.draft ?? String(this.props.value), onFocus: this.onFocus, onMouseDown: this.selectGuard.onMouseDown, onMouseUp: this.selectGuard.onMouseUp, onKeyDown: this.onKeyDown, onChange: this.onChange, onBlur: this.onBlur, title: this.props.commitOn === "blur" ? "Applied when you leave the field or press Enter" : undefined, className: this.props.className }));
    }
}
